import { useState } from 'react';
import { COLOR_PRESETS } from '@/hooks/useStoreSettings';
import { PAGE_BG_PRESETS } from './designConstants';
import { DesignSettings, SectionBgConfig, SectionSaveState } from './designTypes';
import { SaveButton } from './DesignShared';

interface SectionDef {
  key: keyof Pick<DesignSettings, 'sectionProducts' | 'sectionDevices' | 'sectionReviews' | 'sectionContact'>;
  label: string;
  icon: string;
  desc: string;
  defaultBg: string;
}

const SECTIONS: SectionDef[] = [
  { key: 'sectionProducts', label: 'قسم المنتجات',   icon: 'ri-shopping-bag-3-line', desc: 'الشبكة الرئيسية للمنتجات',    defaultBg: '#0a0a0a' },
  { key: 'sectionDevices',  label: 'قسم الأجهزة',    icon: 'ri-computer-line',       desc: 'عرض الأجهزة بالتفصيل',       defaultBg: '#0d0d0d' },
  { key: 'sectionReviews',  label: 'قسم التقييمات',  icon: 'ri-star-line',           desc: 'آراء وتقييمات العملاء',       defaultBg: '#0a0a0a' },
  { key: 'sectionContact',  label: 'قسم التواصل',    icon: 'ri-phone-line',          desc: 'نموذج التواصل والخريطة',      defaultBg: '#0d0d0d' },
];

// Presets including "inherit from page" option
const SECTION_BG_PRESETS = [
  { id: 'inherit',     label: 'من الصفحة',    color: null,      isInherit: true },
  { id: 'black',       label: 'أسود',         color: '#0a0a0a', isInherit: false },
  { id: 'dark-gray',   label: 'رمادي داكن',   color: '#111827', isInherit: false },
  { id: 'charcoal',    label: 'فحمي',         color: '#1c1c1e', isInherit: false },
  { id: 'deep-navy',   label: 'كحلي',         color: '#0d1117', isInherit: false },
  { id: 'dark-green',  label: 'أخضر داكن',   color: '#0a1a0f', isInherit: false },
  { id: 'dark-wine',   label: 'خمري',         color: '#1a0a0a', isInherit: false },
  { id: 'off-white',   label: 'أبيض مائل',   color: '#f8f7f4', isInherit: false },
  { id: 'warm-white',  label: 'أبيض دافئ',   color: '#faf8f5', isInherit: false },
  { id: 'light-gray',  label: 'رمادي فاتح',  color: '#f3f4f6', isInherit: false },
  { id: 'cream',       label: 'كريمي',        color: '#fdf6ec', isInherit: false },
  { id: 'custom',      label: 'مخصص',         color: null,      isInherit: false },
];

interface SectionBgPickerProps {
  label: string;
  icon: string;
  desc: string;
  value: SectionBgConfig;
  onChange: (v: SectionBgConfig) => void;
  selectedColor: typeof COLOR_PRESETS[0];
  pageBgColor: string;
}

function SectionBgPicker({ label, icon, desc, value, onChange, selectedColor, pageBgColor }: SectionBgPickerProps) {
  const [expanded, setExpanded] = useState(false);
  const [customColor, setCustomColor] = useState(value.customColor || '#111111');

  const activeBgColor = value.bgId === 'inherit'
    ? pageBgColor
    : value.bgId === 'custom'
    ? (value.customColor || customColor)
    : SECTION_BG_PRESETS.find(b => b.id === value.bgId)?.color ?? pageBgColor;

  const isLight = ['off-white', 'warm-white', 'light-gray', 'cream'].includes(value.bgId);
  const textOnBg = isLight ? '#1a1a1a' : '#ffffff';
  const subTextOnBg = isLight ? '#6b7280' : '#9ca3af';

  const currentLabel = value.bgId === 'inherit'
    ? 'من الصفحة'
    : value.bgId === 'custom'
    ? value.customColor || 'مخصص'
    : SECTION_BG_PRESETS.find(b => b.id === value.bgId)?.label ?? '—';

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header — clickable to expand */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors text-right"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 flex items-center justify-center rounded-xl border-2 flex-shrink-0"
            style={{
              background: activeBgColor,
              borderColor: value.bgId === 'inherit' ? 'rgba(0,0,0,0.1)' : selectedColor.primary,
            }}
          >
            <i className={`${icon} text-sm`} style={{ color: value.bgId === 'inherit' ? '#9ca3af' : textOnBg }}></i>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-[#1a1a1a]">{label}</p>
            <p className="text-xs text-gray-400">{desc}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-gray-400 hidden sm:block">{currentLabel}</span>
          <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-gray-400 text-sm`}></i>
        </div>
      </button>

      {/* Expanded picker */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-50">
          <p className="text-xs text-gray-400 mt-4 mb-3">اختر لون خلفية هذا القسم:</p>

          {/* Presets grid */}
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-4">
            {SECTION_BG_PRESETS.filter(b => b.id !== 'custom').map(bg => (
              <button
                key={bg.id}
                onClick={() => onChange({ bgId: bg.id, customColor: value.customColor })}
                title={bg.label}
                className="flex flex-col items-center gap-1 cursor-pointer"
              >
                <div
                  className="w-full aspect-square rounded-xl border-2 transition-all duration-200 relative overflow-hidden"
                  style={{
                    background: bg.isInherit
                      ? `linear-gradient(135deg, ${pageBgColor} 50%, #888 50%)`
                      : (bg.color ?? '#111'),
                    borderColor: value.bgId === bg.id ? selectedColor.primary : 'rgba(0,0,0,0.1)',
                    boxShadow: value.bgId === bg.id ? `0 0 0 3px ${selectedColor.primary}40` : '0 2px 4px rgba(0,0,0,0.06)',
                    transform: value.bgId === bg.id ? 'scale(1.08)' : 'scale(1)',
                  }}
                >
                  {bg.isInherit && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className="ri-link-m text-white text-xs drop-shadow"></i>
                    </div>
                  )}
                  {value.bgId === bg.id && !bg.isInherit && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: selectedColor.primary }}>
                        <i className="ri-check-line text-[8px]" style={{ color: selectedColor.textOnPrimary }}></i>
                      </div>
                    </div>
                  )}
                  {value.bgId === bg.id && bg.isInherit && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <i className="ri-check-line text-white text-sm font-black"></i>
                    </div>
                  )}
                </div>
                <span className="text-[9px] text-gray-500 text-center leading-tight whitespace-nowrap">{bg.label}</span>
              </button>
            ))}

            {/* Custom */}
            <button
              onClick={() => onChange({ bgId: 'custom', customColor })}
              title="لون مخصص"
              className="flex flex-col items-center gap-1 cursor-pointer"
            >
              <div
                className="w-full aspect-square rounded-xl border-2 transition-all duration-200 relative overflow-hidden"
                style={{
                  background: `conic-gradient(from 0deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)`,
                  borderColor: value.bgId === 'custom' ? selectedColor.primary : 'rgba(0,0,0,0.1)',
                  boxShadow: value.bgId === 'custom' ? `0 0 0 3px ${selectedColor.primary}40` : '0 2px 4px rgba(0,0,0,0.06)',
                  transform: value.bgId === 'custom' ? 'scale(1.08)' : 'scale(1)',
                }}
              >
                {value.bgId === 'custom' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <i className="ri-check-line text-white text-sm font-black"></i>
                  </div>
                )}
              </div>
              <span className="text-[9px] text-gray-500 whitespace-nowrap">مخصص</span>
            </button>
          </div>

          {/* Custom color input */}
          {value.bgId === 'custom' && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <input
                type="color"
                value={customColor}
                onChange={e => {
                  setCustomColor(e.target.value);
                  onChange({ bgId: 'custom', customColor: e.target.value });
                }}
                className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0.5 bg-transparent"
              />
              <div className="flex-1">
                <p className="text-xs font-bold text-[#1a1a1a] mb-0.5">لون مخصص</p>
                <input
                  type="text"
                  value={customColor}
                  onChange={e => {
                    const val = e.target.value;
                    setCustomColor(val);
                    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
                      onChange({ bgId: 'custom', customColor: val });
                    }
                  }}
                  placeholder="#111111"
                  className="text-xs font-mono text-gray-500 bg-transparent outline-none w-full"
                  dir="ltr"
                  maxLength={7}
                />
              </div>
              <div className="w-8 h-8 rounded-lg border border-gray-200 flex-shrink-0" style={{ background: customColor }}></div>
            </div>
          )}

          {/* Mini preview */}
          <div
            className="rounded-xl p-4 transition-colors duration-300 border"
            style={{ background: activeBgColor, borderColor: 'rgba(128,128,128,0.12)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold" style={{ color: textOnBg }}>{label}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: selectedColor.primary, color: selectedColor.textOnPrimary }}>
                معاينة
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  className="rounded-lg p-2 border"
                  style={{
                    background: isLight ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.05)',
                    borderColor: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
                  }}
                >
                  <div className="h-6 rounded mb-1.5" style={{ background: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)' }}></div>
                  <div className="h-2 rounded w-3/4" style={{ background: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)' }}></div>
                  <div className="h-1.5 rounded w-1/2 mt-1" style={{ background: selectedColor.primary, opacity: 0.7 }}></div>
                </div>
              ))}
            </div>
            {value.bgId === 'inherit' && (
              <p className="text-[10px] mt-2 text-center" style={{ color: subTextOnBg, opacity: 0.6 }}>
                يرث لون خلفية الصفحة الرئيسية
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface Props {
  design: DesignSettings;
  updateDesign: (patch: Partial<DesignSettings>) => void;
  sectionsSaveState: SectionSaveState;
  onSaveSections: () => void;
  selectedColor: typeof COLOR_PRESETS[0];
}

export default function SectionsSection({ design, updateDesign, sectionsSaveState, onSaveSections, selectedColor }: Props) {
  // Compute page bg color for "inherit" preview
  const pageBgPreset = PAGE_BG_PRESETS.find(b => b.id === (design.pageBgId ?? 'black')) ?? PAGE_BG_PRESETS[0];
  const pageBgColor = design.pageBgId === 'custom'
    ? (design.pageBgCustomColor || '#0a0a0a')
    : pageBgPreset.color;

  return (
    <div className="flex flex-col gap-5">
      {/* Header card */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-gradient-to-br from-gray-700 to-gray-500">
            <i className="ri-layout-3-line text-white text-[10px]"></i>
          </div>
          ألوان خلفية الأقسام
        </h3>
        <p className="text-xs text-gray-400 mb-4">
          خصّص لون خلفية كل قسم بشكل مستقل — أو اتركه يرث لون الصفحة الرئيسية
        </p>

        {/* Page bg reference */}
        <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 mb-2">
          <div className="w-8 h-8 rounded-lg border border-gray-200 flex-shrink-0" style={{ background: pageBgColor }}></div>
          <div>
            <p className="text-xs font-bold text-[#1a1a1a]">خلفية الصفحة الحالية</p>
            <p className="text-[10px] text-gray-400 font-mono">{pageBgColor}</p>
          </div>
          <span className="mr-auto text-[10px] text-gray-400 bg-gray-50 px-2 py-1 rounded-full border border-gray-100">
            {pageBgPreset.label}
          </span>
        </div>
      </div>

      {/* Section pickers */}
      <div className="flex flex-col gap-3">
        {SECTIONS.map(section => (
          <SectionBgPicker
            key={section.key}
            label={section.label}
            icon={section.icon}
            desc={section.desc}
            value={design[section.key] as SectionBgConfig}
            onChange={v => updateDesign({ [section.key]: v })}
            selectedColor={selectedColor}
            pageBgColor={pageBgColor}
          />
        ))}
      </div>

      {/* Reset all */}
      <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-[#1a1a1a]">إعادة ضبط الأقسام</p>
            <p className="text-xs text-gray-400">جعل كل الأقسام ترث لون الصفحة</p>
          </div>
          <button
            onClick={() => {
              const reset = { bgId: 'inherit', customColor: '' };
              updateDesign({
                sectionProducts: reset,
                sectionDevices: reset,
                sectionReviews: reset,
                sectionContact: reset,
              });
            }}
            className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl border border-gray-200 text-gray-600 hover:border-gray-400 hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-sm"></i>
            إعادة ضبط
          </button>
        </div>
      </div>

      <SaveButton state={sectionsSaveState} onClick={onSaveSections} label="حفظ ألوان الأقسام" />
    </div>
  );
}
